import {
  SegmentAuthAction,
  SegmentAuthResult,
  type SegmentAuthApiError,
  type SegmentAuthFailure,
  type SegmentAuthOutcome,
} from '@/services/db/segment-in-integration/mutations';

/**
 * How the screen describes the workspace's Segment destination.
 *
 * `Exists` is the state on arrival when a connection is stored: the record says a
 * destination was created, but nothing has checked whether it is still there or
 * still enabled, which is what the verify action is for.
 */
export const ConnectionState = {
  Exists: 'exists',
  Enabled: 'enabled',
  Disabled: 'disabled',
  Deleted: 'deleted',
  Unknown: 'unknown',
} as const;

export type ConnectionState = (typeof ConnectionState)[keyof typeof ConnectionState];

const DESTINATION_NOT_FOUND = 'destination not found';
const SOURCE_MISMATCH = 'source mismatch';

/**
 * What a failed attempt says about the destination.
 *
 * A `404` and a missing destination both mean it is gone. A source mismatch means
 * one exists but belongs to a different Segment source, which the reader has to
 * resolve — the connection is not gone, so it stays `Exists`.
 */
export function stateFromErrors(errors: readonly SegmentAuthApiError[]): ConnectionState | null {
  const first = errors[0];
  if (first == null) return null;

  if (first.detail === DESTINATION_NOT_FOUND || first.status === '404') return ConnectionState.Deleted;
  if (first.detail === SOURCE_MISMATCH) return ConnectionState.Exists;

  return null;
}

function stateFromErroredOutcome(outcome: SegmentAuthOutcome): ConnectionState {
  // A destination that reports itself off is disabled, not missing.
  if (outcome.enabled === false) return ConnectionState.Disabled;

  return stateFromErrors(outcome.errors ?? []) ?? ConnectionState.Unknown;
}

function stateFromOutcome(outcome: SegmentAuthOutcome): ConnectionState {
  switch (outcome.state) {
    case SegmentAuthResult.Created:
    case SegmentAuthResult.Enabled:
      return ConnectionState.Enabled;
    case SegmentAuthResult.Deleted:
      return ConnectionState.Deleted;
    case SegmentAuthResult.Disabled:
      return ConnectionState.Disabled;
    case SegmentAuthResult.Errored:
      return stateFromErroredOutcome(outcome);
    default: {
      const exhaustive: never = outcome.state;
      return exhaustive;
    }
  }
}

export type ConnectionStatusInput = {
  /** The most recent attempt on this screen, or null before one is made. */
  latestOutcome: SegmentAuthOutcome | null;
  /** Why the most recent attempt threw, when it did. */
  latestFailure: { failure: SegmentAuthFailure; errors: readonly SegmentAuthApiError[] } | null;
  /** Whether the workspace has a stored connection. */
  hasStoredConnection: boolean;
};

/**
 * The screen's connection state.
 *
 * The most recent attempt wins: once the reader has connected, verified or
 * disabled on this screen, that result describes the destination better than the
 * stored record, which only says one was created at some point.
 */
export function deriveConnectionState({
  latestOutcome,
  latestFailure,
  hasStoredConnection,
}: ConnectionStatusInput): ConnectionState {
  if (latestOutcome != null) return stateFromOutcome(latestOutcome);

  // Only a failure that says what the destination *is* changes the state. A
  // mismatch says the attempt could not be made, not that the connection is gone,
  // so the stored record still describes it.
  if (latestFailure != null) {
    const fromErrors = stateFromErrors(latestFailure.errors);
    if (fromErrors != null) return fromErrors;
  }

  return hasStoredConnection ? ConnectionState.Exists : ConnectionState.Unknown;
}

/** Which actions the Options menu can offer. Ember hid the menu outside these states. */
export function isDeleteDisablePossible(state: ConnectionState): boolean {
  return state === ConnectionState.Exists || state === ConnectionState.Enabled || state === ConnectionState.Disabled;
}

/** The sentence the panel opens with, which names what the next action will do. */
export function connectionDescriptionPrefix(state: ConnectionState): string {
  switch (state) {
    case ConnectionState.Exists:
      return 'Verify connection to your';
    case ConnectionState.Enabled:
      return 'Connected to';
    case ConnectionState.Disabled:
      return 'Re-enable connection to your';
    case ConnectionState.Deleted:
    case ConnectionState.Unknown:
      return 'Connect your';
    default: {
      const exhaustive: never = state;
      return exhaustive;
    }
  }
}

/**
 * What the main button does next, which follows from the state rather than from a
 * separate control: a stored-but-unchecked destination is verified, a disabled one
 * is re-enabled, and anything else is created.
 */
export function oauthActionForState(state: ConnectionState): SegmentAuthAction {
  if (state === ConnectionState.Exists) return SegmentAuthAction.Check;
  if (state === ConnectionState.Disabled) return SegmentAuthAction.Enable;

  return SegmentAuthAction.Create;
}

/** The main control's label, which names the action the state implies. */
export function connectionButtonLabel(state: ConnectionState): string {
  switch (state) {
    case ConnectionState.Exists:
      return 'Verify Connection Status';
    case ConnectionState.Enabled:
      return 'Connected to Segment';
    case ConnectionState.Disabled:
      return 'Re-enable Segment';
    case ConnectionState.Deleted:
    case ConnectionState.Unknown:
      return 'Configure Segment';
    default: {
      const exhaustive: never = state;
      return exhaustive;
    }
  }
}

/**
 * Which credential the actions use.
 *
 * A workspace with exactly one has it chosen for it — the picker is shown disabled,
 * explaining that there is nothing to choose between — so the connect button is
 * reachable without the reader touching it. With several, nothing is assumed.
 */
export function resolveSelectedCredentialId<T extends { id: string }>(
  credentials: readonly T[],
  chosenId: string | null,
): string | null {
  const only = credentials.length === 1 ? credentials[0] : undefined;
  if (only != null) return only.id;

  return chosenId;
}

/**
 * The Segment source as the screen names it.
 *
 * The API sends a full path — `workspaces/<workspace>/sources/<source>` — and the
 * copy shows only the two identifying segments. A path that does not have that
 * shape is shown as-is rather than mangled into `undefined/undefined`.
 */
export function parseSegmentSourceName(fullPath: string | null | undefined): string | null {
  if (fullPath == null || fullPath === '') return null;

  const [, workspace, , source] = fullPath.split('/');
  if (workspace == null || source == null) return fullPath;

  return `${workspace}/${source}`;
}
